/**
 * AddToDealDialog — searchable deal picker that links a contact's company
 * to an existing deal via POST /api/companies/:companyId/deal-links.
 *
 * Context: in this CRM, contacts don't link to deals directly — they go
 * through their company via deal_company_links (same pattern the old
 * Deals page used before it was removed in the March 2026 cleanup). So
 * "Add Andy Holmbo to Project Overlay" means "add Andy's company to the
 * Project Overlay deal as a linked company (typically a buyer)".
 *
 * Behavior:
 * - Fetches /api/deals (active status filtered client-side)
 * - Fetches /api/companies/:companyId/deals to find already-linked deals
 * - Filters already-linked deals out of the picker list
 * - Shows deal name + type + stage + primary company (if available)
 * - On pick: POST to link, toast success, close
 *
 * Caller is responsible for only opening the dialog when the contact
 * HAS a company — if companyId is missing, the menu item should be
 * disabled with a tooltip explaining why.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Briefcase, Check, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";

interface Deal {
  id: string;
  name: string;
  type?: string | null;
  stage?: string | null;
  status?: string | null;
  industry?: string | null;
}

interface AddToDealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Contact's company id — deal_company_links are created against this. */
  companyId: string;
  /** Contact's name, for copy in the dialog description. */
  contactName: string;
  /** Company category, used to default the role of the new link. */
  companyCategory?: string;
  /** Called after a successful link so the caller can invalidate any caches. */
  onLinked?: (deal: Deal) => void;
}

export function AddToDealDialog({
  open,
  onOpenChange,
  companyId,
  contactName,
  companyCategory,
  onLinked,
}: AddToDealDialogProps) {
  const [submitting, setSubmitting] = useState<string | null>(null);

  // All deals the CRM knows about. Filter to active-ish statuses below.
  const { data: allDeals = [], isLoading: dealsLoading } = useQuery<Deal[]>({
    queryKey: ["deals-picker"],
    queryFn: async () => {
      const res = await fetch("/api/deals", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load deals");
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: open,
    staleTime: 60_000,
  });

  // Deals this company is already on. These are filtered out of the picker
  // below so the user isn't tempted to create duplicate links.
  const { data: existingLinks = [], isLoading: linksLoading } = useQuery<Array<{ id: string }>>({
    queryKey: ["company-deals", companyId, "for-picker"],
    queryFn: async () => {
      const res = await fetch(`/api/companies/${companyId}/deals`, { credentials: "include" });
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: open && !!companyId,
    staleTime: 30_000,
  });

  const isLoading = dealsLoading || linksLoading;

  const alreadyLinkedIds = new Set(existingLinks.map(d => d.id));
  const activeDeals = allDeals.filter(d => {
    if (!d.status || d.status === "active") {
      // active or unset status → include
      return !alreadyLinkedIds.has(d.id);
    }
    return false;
  });

  // Default role by the contact's company category. Matches what the server
  // accepts on deal_company_links.role — Buyer is the most common case and
  // also the server's default, so we only override when the category
  // clearly implies something else.
  const roleForCategory = (cat?: string): string => {
    switch (cat) {
      case "Capital Providers": return "Capital Provider";
      case "COI": return "Advisor";
      case "Client": return "Client";
      case "Prospects":
      case "Buyer Network":
      default: return "Buyer";
    }
  };

  const handlePick = async (deal: Deal) => {
    if (submitting) return;
    setSubmitting(deal.id);
    try {
      const res = await fetch(`/api/companies/${companyId}/deal-links`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dealId: deal.id,
          role: roleForCategory(companyCategory),
          // Intentionally not setting stage/status — let the server default
          // them. Users can edit on the deal detail if needed.
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Added to ${deal.name}`);
      onLinked?.(deal);
      onOpenChange(false);
    } catch {
      toast.error("Failed to link to deal");
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>Add {contactName} to a deal</DialogTitle>
          <DialogDescription>
            {contactName}'s company will be linked to the deal you pick as a{" "}
            {roleForCategory(companyCategory).toLowerCase()}. You can change
            the role and stage from the deal afterward.
          </DialogDescription>
        </DialogHeader>
        <Command className="border-0">
          <CommandInput placeholder="Search active deals..." autoFocus />
          <CommandList className="max-h-[400px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Loading deals...
              </div>
            ) : activeDeals.length === 0 ? (
              <CommandEmpty>
                {allDeals.length === 0
                  ? "No deals in the CRM yet."
                  : alreadyLinkedIds.size === allDeals.filter(d => d.status === "active" || !d.status).length
                    ? "This company is already linked to every active deal."
                    : "No active deals available."}
              </CommandEmpty>
            ) : (
              <>
                <CommandEmpty>No deals match your search.</CommandEmpty>
                {activeDeals.map(deal => {
                  const isSubmitting = submitting === deal.id;
                  return (
                    <CommandItem
                      key={deal.id}
                      value={`${deal.name} ${deal.type || ""} ${deal.stage || ""} ${deal.industry || ""}`}
                      onSelect={() => handlePick(deal)}
                      className="px-4 py-3 cursor-pointer"
                      disabled={!!submitting}
                    >
                      <Briefcase className="w-4 h-4 mr-3 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{deal.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {[deal.type, deal.stage, deal.industry].filter(Boolean).join(" · ") || "no stage"}
                        </div>
                      </div>
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin ml-2" />
                      ) : (
                        <Check className="w-4 h-4 ml-2 opacity-0 group-data-[selected=true]:opacity-100" />
                      )}
                    </CommandItem>
                  );
                })}
              </>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
